<script setup>
import { Link, usePage, router } from '@inertiajs/vue3';
import { Download, FileText, HelpCircle, Info, LogOut, Map, Megaphone, User, UserCircle, Bell } from '@lucide/vue';
import { computed, ref, getCurrentInstance } from 'vue';
import HulpvraagModal from '../HulpvraagModal.vue';
import SupportModal from '../SupportModal.vue';
import GlobalSearch from '../GlobalSearch.vue';

// Access global properties for route function in SSR/CSR
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

const page = usePage();
const user = computed(() => page.props.auth.user);
const can = computed(() => page.props.auth.user.can || {});
const openActionsCount = computed(() => user.value?.open_actions_count ?? 0);

// Exports in the topbar (phase 5a). Record-driven, not session-driven: a
// 20-minute export outlives the tab it was requested from. The icon shows
// while the user has any visible record in Mijn exports (their entry point
// to the list; rows they hid don't count), the badge counts files that
// arrived and were not yet seen — same pattern as the bell.
const hasVisibleExports = computed(() => user.value?.has_visible_exports ?? false);
const readyExportsCount = computed(() => user.value?.ready_exports_count ?? 0);

// Control user menu visibility
const isUserMenuVisible = ref(false);
let menuTimeout = null;

// Control hulpvraag modal visibility
const showHulpvraagModal = ref(false);

// Control support modal visibility
const showSupportModal = ref(false);

const showUserMenu = () => {
    if (menuTimeout) clearTimeout(menuTimeout);
    isUserMenuVisible.value = true;
};

const hideUserMenu = () => {
    // Delay hiding to allow moving mouse to menu
    menuTimeout = setTimeout(() => {
        isUserMenuVisible.value = false;
    }, 200); // Adjust delay as needed
};

const cancelHideUserMenu = () => {
    if (menuTimeout) clearTimeout(menuTimeout);
};

const openHulpvraagModal = () => {
    showHulpvraagModal.value = true;
};

const closeHulpvraagModal = () => {
    showHulpvraagModal.value = false;
};

const openSupportModal = () => {
    showSupportModal.value = true;
};

const closeSupportModal = () => {
    showSupportModal.value = false;
};

// Navigate to dashboard and trigger guide tour
const startGuideTour = () => {
    isUserMenuVisible.value = false
    router.visit(route('dashboard.index') + '?start_tour=1')
}


</script>
<template>
    <header class="header shrink-0">
        <div class="search-container">
            <GlobalSearch />
        </div>

        <div class="user-controls">
            <button id="button-header-support" class="icon-button" @click="openSupportModal" v-if="can.viewInfoModal">
                <Info />
            </button>
            <button id="button-header-help" class="icon-button" @click="openHulpvraagModal">
                <HelpCircle />
            </button>
            <Link
                v-if="hasVisibleExports"
                id="button-header-ready-exports"
                :href="route('reports.exports.index')"
                class="icon-button notification-button"
                :title="readyExportsCount > 0 ? `Exports klaar om te downloaden (${readyExportsCount})` : 'Mijn exports'"
            >
                <Download />
                <span v-if="readyExportsCount > 0" class="badge badge--counter">{{ readyExportsCount }}</span>
            </Link>
            <Link id="button-header-open-actions" :href="route('account.actions.index')" class="icon-button notification-button" :title="`Openstaande acties (${openActionsCount})`">
                <Bell />
                <span v-if="openActionsCount > 0" class="badge badge--counter">{{ openActionsCount }}</span>
            </Link>
            <div class="user-dropdown" @mouseleave="hideUserMenu">
                <button id="button-header-user-menu" class="icon-button" @mouseenter="showUserMenu">
                    <UserCircle />
                    <span class="user-name">{{ user.fullname }}</span>
                </button>
                <div class="user-menu" v-show="isUserMenuVisible" @mouseenter="cancelHideUserMenu" @mouseleave="hideUserMenu">
                    <Link id="button-header-menu-profile" :href="route('profile.index')">
                        <User :size="20" />
                        <span>Profiel</span>
                    </Link>
                    <Link id="button-header-menu-releasenotes" :href="route('releasenotes.index')">
                        <Megaphone :size="20" />
                        <span>Releasenotes</span>
                    </Link>
                    <Link id="button-header-menu-actions" :href="route('account.actions.index')">
                        <FileText :size="20" />
                        <span>Acties</span>
                    </Link>
                    <a id="button-header-menu-start-guide-tour" href="#" @click.prevent="startGuideTour">
                        <Map :size="20" />
                        <span>Tour starten</span>
                    </a>
                    <Link id="button-header-menu-logout" :href="route('logout')">
                        <LogOut :size="20" />
                        <span>Uitloggen</span>
                    </Link>
                </div>
            </div>
        </div>
    </header>

    <!-- Hulpvraag Modal -->
    <HulpvraagModal
        :show="showHulpvraagModal"
        @close="closeHulpvraagModal"
    />

    <!-- Support Modal -->
    <SupportModal
        :show="showSupportModal"
        @close="closeSupportModal"
    />
</template>

<style scoped>
/* Using consolidated layout styles from our new CSS architecture */
/* Component-specific overrides only */
</style>
